import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import HomeSearchPage from './components/CarSearch/HomeSearchPage';
import Login from './components/Login';
import Signup from './components/Signup';
import PublicLayout from './layouts/PublicLayout';
import Dashboard from './components/Dashboard';
import AuthLayout from './layouts/AuthLayout';
import useAuth from './hooks/useAuth';
import Settings from './components/Settings/Settings';
import Appointments from './components/Appointments/Appointments';
import Inventory from './components/Inventory/Inventory';
import Offers from './components/Offers/Offers';
import UserLogin from './components/UserLogin';
import UserSignup from './components/UserSignup';
import Compare from './components/CarSearch/CarComparison';
import CarDetails from './components/CarSearch/CarDetails';
import DealerInventory from './components/CarSearch/DealerInventory';
import NotFound from './components/NotFound';
import UserDashboard from './components/UserDashboard/UserDashboard';
import UserChatHistory from './components/UserDashboard/UserChatHistory';
import UserChat from './components/UserDashboard/UserChat';
import DealerChat from './components/DealerChat/DealerChat';
import Chats from './pages/Chats';
import ImportDashboard from './components/Import/ImportDashboard';
import useImportSync from './hooks/useImportSync';

// Protected Route Component
const ProtectedRoute = ({ children, requireDealer = false, requireUser = false }) => {
  const { isDealerAuthenticated, isUserAuthenticated } = useAuth();
  
  if (requireDealer && !isDealerAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  if (requireUser && !isUserAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Auth Check Component
const AuthCheck = () => {
  const { isDealerAuthenticated, isUserAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Only redirect if we're on a protected route and not authenticated
    const currentPath = window.location.pathname;
    const protectedDealerRoutes = ['/dashboard', '/settings', '/appointments', '/offers', '/inventory'];
    const protectedUserRoutes = ['/user/dashboard'];
    
    const isOnProtectedDealerRoute = protectedDealerRoutes.includes(currentPath);
    const isOnProtectedUserRoute = protectedUserRoutes.includes(currentPath);
    
    if ((isOnProtectedDealerRoute && !isDealerAuthenticated) || 
        (isOnProtectedUserRoute && !isUserAuthenticated)) {
      navigate('/', { replace: true });
    }
  }, [isDealerAuthenticated, isUserAuthenticated, navigate]);
  
  return null;
};

const App = () => {
  const { isAuthenticated, isDealerAuthenticated, isUserAuthenticated, isDealer, isUser } = useAuth();
  
  // Auto-sync import status on every page load
  useImportSync();

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Router>
        <AuthCheck />
        <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <PublicLayout>
            <HomeSearchPage />
          </PublicLayout>
        } />
        <Route path="/login" element={
          <PublicLayout>
            <Login />
          </PublicLayout>
        } />
        <Route path="/signup" element={
          <PublicLayout>
            <Signup />
          </PublicLayout>
        } />
        <Route path="/user/login" element={
          <PublicLayout>
            <UserLogin />
          </PublicLayout>
        } />
        <Route path="/user/signup" element={
          <PublicLayout>
            <UserSignup />
          </PublicLayout>
        } />
        <Route path="/compare" element={
          <PublicLayout>
            <Compare />
          </PublicLayout>
        } />
        <Route path="/car-details" element={
          <PublicLayout>
            <CarDetails />
          </PublicLayout>
        } />
        <Route path="/dealer-inventory/:dealerId" element={
          <PublicLayout>
            <DealerInventory />
          </PublicLayout>
        } />
        
        {/* Hidden Import Dashboard Route */}
        <Route path="/admin/import-dashboard" element={
          <ImportDashboard />
        } />
        
        {/* Protected Dealer Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute requireDealer={true}>
            <AuthLayout>
              <Dashboard />
            </AuthLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute requireDealer={true}>
            <AuthLayout>
              <Settings />
            </AuthLayout>
          </ProtectedRoute>
        } />
        <Route path="/appointments" element={
          <ProtectedRoute requireDealer={true}>
            <AuthLayout>
              <Appointments />
            </AuthLayout>
          </ProtectedRoute>
        } />
        <Route path="/offers" element={
          <ProtectedRoute requireDealer={true}>
            <AuthLayout>
              <Offers />
            </AuthLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory" element={
          <ProtectedRoute requireDealer={true}>
        <AuthLayout>
              <Inventory />
        </AuthLayout>
          </ProtectedRoute>
        } />
        <Route path="/chats" element={
          <ProtectedRoute requireDealer={true}>
            <AuthLayout>
              <Chats />
            </AuthLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/dealer/room/:roomId" element={
          <ProtectedRoute requireDealer={true}>
            <DealerChat />
          </ProtectedRoute>
        } />
        <Route path="/chat/:roomId" element={
          <ProtectedRoute requireDealer={true}>
            <AuthLayout>
              <DealerChat />
            </AuthLayout>
          </ProtectedRoute>
        } />
      
        
        {/* Protected User Routes */}
        <Route path="/user/dashboard" element={
          <ProtectedRoute requireUser={true}>
            <PublicLayout>
              <UserDashboard />
            </PublicLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/user/chats" element={
          <ProtectedRoute requireUser={true}>
            <PublicLayout>
              <UserChatHistory />
            </PublicLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/user/chat/:sessionId" element={
          <ProtectedRoute requireUser={true}>
            <PublicLayout>
              <UserChat />
            </PublicLayout>
          </ProtectedRoute>
        } />
        
        {/* Catch all route */}
        <Route path="*" element={
        <PublicLayout>
            <NotFound />
          </PublicLayout>
        } />
          </Routes>
      </Router>
    </GoogleOAuthProvider>
  );
};

export default App;
